import { useEffect, useRef, useState } from "react";
import { ScanBarcode, Plus, ChevronLeft, ChevronRight, Heart, History, Car, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { GlassCard, EmptyState, fmtMoney } from "@/components/erp";
import { UrunGorsel } from "@/components/UrunGorsel";
import { favoriDegistir } from "@/components/bayi/BayiFavoriler";
import { Vurgu, sonSecilenler, sonGetir, sonaEkle, ayniKod } from "@/lib/ara";
import { BarkodKamera } from "@/components/bayi/BarkodKamera";
import { bipTamam, bipHata } from "@/lib/bip";
import { katalogAra, agHatasi, useCevrimici } from "@/lib/katalog";

export const yilYazi = (a) => (a.yil_bas && a.yil_bit ? (a.yil_bas === a.yil_bit ? `${a.yil_bas}` : `${a.yil_bas}–${a.yil_bit}`) : a.yil_bas ? `${a.yil_bas}+` : a.yil_bit ? `–${a.yil_bit}` : "");

export const FiyatUyari = ({ testid }) => (
  <div className="text-[10px] text-amber-300 flex items-center gap-1" data-testid={testid}><WifiOff className="w-3 h-3 shrink-0" />Güncel fiyat için internet bağlantısı gerekir</div>
);

const Araclar = ({ u, q }) => {
  const l = u.araclar || [];
  if (!l.length) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid={`bayi-araclar-${u.kod}`}>
      {l.slice(0, 4).map((a, i) => (
        <span key={i} className="inline-flex items-start gap-1 max-w-full text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
          <Car className="w-3 h-3 opacity-60 shrink-0 mt-px" /><span className="min-w-0 break-words"><Vurgu text={[a.marka, a.model, yilYazi(a), a.motor].filter(Boolean).join(" ")} q={q} /></span></span>
      ))}
      {l.length > 4 && <span className="text-[10px] text-muted-foreground px-1 py-0.5" title={l.slice(4).map((a) => [a.marka, a.model, yilYazi(a)].filter(Boolean).join(" ")).join(", ")}>+{l.length - 4} araç</span>}
    </div>
  );
};
import api from "@/lib/api";

export const UrunKart = ({ u, pb, onEkle, q, odak, cevrimdisi }) => {
  const [m, setM] = useState(1);
  const [fav, setFav] = useState(!!u.favori);
  const miktar = useRef(null);
  useEffect(() => { if (odak) { miktar.current?.focus(); miktar.current?.select(); } }, [odak]);
  const kalp = async () => { if (await favoriDegistir(u, !fav)) setFav(!fav); };
  const ekle = () => { if (u.fiyat == null || !(Number(m) > 0)) return; onEkle(u, Number(m)); setM(1); };
  return (
    <GlassCard className={`p-4 flex gap-4 items-start relative min-w-0 ${odak ? "ring-2 ring-[var(--g1)]" : ""}`} data-testid={`bayi-urun-${u.kod}`}>
      <button data-testid={`bayi-favori-${u.kod}`} onClick={kalp} title={fav ? "Favorilerden çıkar" : "Favorilere ekle"} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-transform active:scale-90">
        <Heart className={`w-4 h-4 ${fav ? "fill-rose-400 text-rose-400" : "text-muted-foreground"}`} /></button>
      <UrunGorsel id={u.id} var={u.gorsel} className="w-20 h-20" />
      <div className="min-w-0 flex-1 space-y-1.5 pr-6">
        <div className="font-mono text-[11px] text-muted-foreground"><Vurgu text={u.kod} q={q} /></div>
        <div className="text-sm font-medium leading-snug line-clamp-2"><Vurgu text={u.ad} q={q} /></div>
        <div className="text-xs text-muted-foreground truncate">{[u.olcu, u.marka].filter(Boolean).join(" · ")}</div>
        <Araclar u={u} q={q} />
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className={`text-xs ${u.stok > 0 ? "text-emerald-300" : "text-amber-300"}`} data-testid={`bayi-stok-${u.kod}`}>
            {u.stok > 0 ? `Stok: ${u.stok} ${u.birim}` : "Stokta yok · üretimden"}</span>
          <span className="text-right" data-testid={`bayi-fiyat-${u.kod}`}>
            {u.kampanya && <span className="block text-[10px] text-rose-300" data-testid={`bayi-kampanya-${u.kod}`}>%{u.kampanya.yuzde} kampanya · <s className="text-muted-foreground">{fmtMoney(u.liste_fiyat, pb)}</s></span>}
            <span className={`font-mono font-semibold ${u.kampanya ? "text-rose-200" : ""}`}>{u.fiyat == null ? "—" : fmtMoney(u.fiyat, pb)}</span></span>
        </div>
        {cevrimdisi && <FiyatUyari testid={`bayi-fiyat-uyari-${u.kod}`} />}
        <div className="flex items-center gap-2 pt-1">
          <Input ref={miktar} type="number" min="1" value={m} onChange={(e) => setM(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ekle(); } }} className="h-8 w-20 bg-white/5" data-testid={`bayi-miktar-${u.kod}`} />
          <button data-testid={`bayi-ekle-${u.kod}`} disabled={u.fiyat == null || !(Number(m) > 0)} onClick={ekle}
            className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs whitespace-nowrap grad-bg text-white disabled:opacity-40 transition-transform active:scale-95">
            <Plus className="w-3.5 h-3.5" />Sepete ekle</button>
        </div>
      </div>
    </GlassCard>
  );
};

const SonSecilenler = ({ liste, onSec }) => (
  <div className="absolute z-20 left-0 right-0 top-full mt-1 rounded-xl glass border border-white/10 p-1 shadow-xl" data-testid="bayi-son-liste">
    <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground"><History className="w-3 h-3" />Son seçilenler</div>
    {liste.map((x) => (
      <button key={x.kod} type="button" data-testid={`bayi-son-${x.kod}`} onMouseDown={(e) => { e.preventDefault(); onSec(x.kod); }}
        className="w-full flex gap-2 items-baseline text-left px-2 py-1.5 rounded text-sm hover:bg-white/10">
        <span className="font-mono text-xs text-muted-foreground shrink-0">{x.kod}</span><span className="truncate">{x.ad}</span></button>
    ))}
  </div>
);

export const BayiUrunler = ({ pb, onEkle }) => {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [kod, setKod] = useState("");
  const [tarama, setTarama] = useState(0);
  const [odak, setOdak] = useState("");
  const [acik, setAcik] = useState(false);
  const [stokta, setStokta] = useState(false);
  const [sayfa, setSayfa] = useState(1);
  const [d, setD] = useState(null);
  const ara = useRef(null);
  const cevrimici = useCevrimici();
  const [sonDeger, setSonDeger] = useState(() => sonSecilenler(user.id, "bayi-urun"));
  const sonlar = acik && !q.trim() ? sonDeger : [];
  useEffect(() => { if (acik) { sonGetir(user.id, "bayi-urun").then(setSonDeger); } }, [acik, user.id]);
  useEffect(() => { setSayfa(1); }, [q, stokta]);
  useEffect(() => {
    let iptal = false;
    const isle = (data) => {
      if (iptal) return;
      if (kod && !data.urunler.length) {
        if (!/\s/.test(kod)) { bipHata(); toast.error(`"${kod}" stok koduyla ürün bulunamadı`); }
        setKod(""); return;
      }
      setD(data);
      if (kod) { bipTamam(); setOdak(data.urunler[0].kod); }
    };
    const t = setTimeout(() => {
      const p = kod ? { kod, stokta } : { q, sayfa, stokta };
      api.get("/bayi/urunler", { params: p }).then((r) => isle(r.data)).catch((e) => {
        const yerel = agHatasi(e) && katalogAra(user.id, p);
        if (yerel) isle(yerel);
        else if (agHatasi(e) && !iptal) toast.error("İnternet yok ve ürün listesi henüz telefona kaydedilmedi");
      });
    }, kod ? 0 : 200);
    return () => { iptal = true; clearTimeout(t); };
  }, [q, sayfa, stokta, kod, tarama, user.id, cevrimici]);
  const yaz = (v) => { setQ(v); setKod(""); setOdak(""); };
  const kodla = (v) => { setQ(v); setOdak(""); setKod(v.trim()); setTarama((n) => n + 1); setAcik(false); };
  const ekle = (u, m) => {
    onEkle(u, m);
    sonaEkle(user.id, "bayi-urun", { kod: u.kod, ad: u.ad }, (a, b) => ayniKod(a.kod, b.kod));
    setSonDeger(sonSecilenler(user.id, "bayi-urun"));
    if (odak) { setOdak(""); ara.current?.focus(); ara.current?.select(); }
  };
  const son = d ? Math.max(1, Math.ceil(d.toplam / d.sayfa_boyu)) : 1;
  return (
    <div className="space-y-4" data-testid="bayi-urunler">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <ScanBarcode className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input ref={ara} data-testid="bayi-ara" placeholder="Kod, ürün adı, araç (ör. passat 2010) ara veya barkod okutun…" value={q} onChange={(e) => yaz(e.target.value)}
            onFocus={() => setAcik(true)} onBlur={() => setAcik(false)} onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { e.preventDefault(); kodla(q); } }} className="pl-9 h-11 bg-white/5" />
          {sonlar.length > 0 && <SonSecilenler liste={sonlar} onSec={kodla} />}
        </div>
        <BarkodKamera onOku={kodla} />
        <label className="flex items-center gap-2 text-sm text-muted-foreground"><Switch data-testid="bayi-stokta" checked={stokta} onCheckedChange={setStokta} />Sadece stoktakiler</label>
      </div>
      {d && q.trim() && <div className="text-xs text-muted-foreground" data-testid="bayi-sonuc-sayi">“{q.trim()}” için {d.toplam} ürün</div>}
      {d && d.urunler.length === 0 && <EmptyState text="Aramanıza uygun ürün bulunamadı." testid="bayi-urun-bos" />}
      <div className="grid sm:grid-cols-2 2xl:grid-cols-3 gap-3">{d?.urunler.map((u) => <UrunKart key={u.id} u={u} pb={pb} onEkle={ekle} q={q} odak={odak === u.kod} cevrimdisi={!!d.cevrimdisi} />)}</div>
      {d && d.toplam > d.sayfa_boyu && (
        <div className="flex items-center justify-center gap-3 text-sm" data-testid="bayi-sayfalama">
          <button data-testid="bayi-onceki" disabled={sayfa <= 1} onClick={() => setSayfa(sayfa - 1)} className="p-2 rounded-full bg-white/5 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-muted-foreground">{sayfa} / {son} · {d.toplam} ürün</span>
          <button data-testid="bayi-sonraki" disabled={sayfa >= son} onClick={() => setSayfa(sayfa + 1)} className="p-2 rounded-full bg-white/5 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};
